/**
 * Wellness-scope copy rules (docs/LEGAL_PRIVACY.md §4) in one place, so the UI
 * strings, the LLM system prompt and the output filter can never drift apart.
 *
 * FDA General Wellness stays available only while we make no disease-specific
 * diagnosis/treatment claim. Everything here is enforced by
 * `findBannedPhrases()` in tests and in the post-processing of every LLM answer.
 */
/** Phrases that must never appear in product copy or model output. */
export const BANNED_PHRASES = [
    // diagnosis / treatment claims
    'diagnose',
    'diagnosis',
    'diagnostic',
    'treat',
    'treatment',
    'cure',
    'heal your',
    'therapy for',
    'therapeutic',
    'prescribe',
    'prescription',
    'medical device',
    'clinically proven',
    'clinical-grade',
    'clinical grade',
    'medical advice',
    // symptom / disease claims
    'relieve your pain',
    'pain relief',
    'reduce your pain',
    'fix your posture',
    'cure tech neck',
    'treat tech neck',
    'disease',
    'disorder',
    'syndrome',
    'pathology',
    'injury will',
    // outcome guarantees / growth claims
    'guaranteed',
    'will eliminate',
    'height increase',
    'grow taller',
    'height-maxxing',
    'heightmaxxing',
    // research framing — forbidden everywhere (LEGAL §0, store human-subjects rules)
    'research study',
    'research participant',
    'clinical trial',
    'irb',
];
/**
 * Words that are fine in normal English but must never appear in *our* strings
 * because the app store / state health-data laws read them as a medical posture.
 * Kept separate so the filter can report them with a softer flag.
 */
export const DISCOURAGED_WORDS = ['research', 'study', 'patient', 'symptom'];
/** Case-insensitive, word-boundary aware scan used by the LLM output filter. */
export function findBannedPhrases(text) {
    const hits = [];
    const haystack = text.toLowerCase();
    const scan = (list, severity) => {
        for (const phrase of list) {
            const needle = phrase.toLowerCase();
            let from = 0;
            for (;;) {
                const index = haystack.indexOf(needle, from);
                if (index === -1)
                    break;
                const before = index === 0 ? ' ' : haystack[index - 1];
                const afterIdx = index + needle.length;
                const after = afterIdx >= haystack.length ? ' ' : haystack[afterIdx];
                const isBoundary = !/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after);
                if (isBoundary)
                    hits.push({ phrase, index, severity });
                from = index + needle.length;
            }
        }
    };
    scan(BANNED_PHRASES, 'banned');
    scan(DISCOURAGED_WORDS, 'discouraged');
    return hits.sort((a, b) => a.index - b.index);
}
/** Fixed strings that must appear verbatim where the docs require them. */
export const DISCLAIMERS = {
    /** Bottom of every report and exercise screen. */
    short: 'Stop if you feel pain. General information, not medical advice.',
    /** With every exercise's instructions (PT list, 2026-10-16: precautions if a move hurts). */
    exercise: 'Move within a comfortable range. If a move causes pain, makes pain worse, or brings on tingling, numbness or dizziness, stop and talk to a physical therapist or doctor before doing it again.',
    /** Full disclaimer block (legal pages, onboarding). */
    long: 'Erectus is a wellness and educational tool. It is not a medical device and does not ' +
        'diagnose, treat, or prevent any condition. Angle estimates carry roughly ±5° of error. ' +
        'If you have pain or a health concern, talk to a physical therapist or doctor.',
    /** Shown next to any angle number. */
    accuracy: 'Estimated angle, ±5° margin.',
    /** Appended to every LLM coaching answer. */
    coachingFooter: 'Stop if you feel pain and talk to a physical therapist.',
};
/** Consent document versions recorded on `users/{uid}.consent`. */
export const CONSENT_VERSIONS = {
    tos: '2026-09-24',
    privacy: '2026-09-24',
    chdp: '2026-09-24',
    // 2026-10-07: turning sharing on also sends the sessions still on the phone (last 7 days).
    dataOptIn: '2026-10-07',
};
export const AGE_GATES = {
    /** COPPA: under 13 cannot create an account at all. */
    minimumAccountAge: 13,
    /** Detection-improvement data sharing is 18+ only (LEGAL §3). */
    dataOptInAge: 18,
};
/**
 * Who can open /admin. Checked by every admin function (the real wall) and by
 * the app (cosmetic). Change it here and redeploy both.
 */
export const ADMIN_EMAILS = ['uihyunkei@gmail.com', 'xjcl1324@gmail.com', 'mminha93@gmail.com'];
//# sourceMappingURL=copy.js.map